import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Empty, ErrorCard, LOGIN_NEEDED, PillTag, SkeletonCard, Toggle } from '@/components/ui';
import { useApi } from '@/lib/api';
import { cx } from '@/lib/cx';
import type { Delta } from '@/lib/format';
import { countDelta, fmtInt, fmtMoney, fmtPct, rateDelta } from '@/lib/format';
import type { Digest, DigestList } from '@/lib/types';

const SECTIONS = ['What changed', 'Problems', 'Unmet demand', 'Accounts to watch'];

function KpiTile({ label, value, delta }: { label: string; value: string; delta: Delta | null }) {
  return (
    <div className="card">
      <div className="k muted" style={{ fontSize: 12, fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
      {delta && <span className={cx('d', delta.tone)}>{delta.text}</span>}
    </div>
  );
}

function DigestBody({ kind, start }: { kind: string; start: string }) {
  const query = useApi<Digest>(`/digests/${kind}`, { start });
  if (query.error) return <ErrorCard error={query.error} />;
  if (!query.data) return <SkeletonCard height={300} />;
  const d = query.data;
  const k = d.kpis;
  return (
    <>
      <div className="faint" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.05em' }}>
        {kind === 'week' ? 'WEEKLY' : 'MONTHLY'} DIGEST · {d.region} · {d.period.label.toUpperCase()}
      </div>
      <div className="h1" style={{ margin: '4px 0 14px' }}>
        {d.headline}
      </div>
      <div className="kpis k4">
        <KpiTile label="AI asks" value={fmtInt(k.asks.value)} delta={countDelta(k.asks.value, k.asks.prev)} />
        <KpiTile
          label="Active companies"
          value={fmtInt(k.companies.value)}
          delta={countDelta(k.companies.value, k.companies.prev)}
        />
        <KpiTile
          label="Not-ok rate"
          value={fmtPct(k.not_ok_rate.value)}
          delta={rateDelta(k.not_ok_rate.value, k.not_ok_rate.prev)}
        />
        <KpiTile
          label="AI spend"
          value={fmtMoney(k.spend.value)}
          delta={countDelta(k.spend.value, k.spend.prev, false)}
        />
      </div>
      <h3>What changed</h3>
      {d.changes.length ? (
        <ul>
          {d.changes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      ) : (
        <div className="muted">No big changes by area.</div>
      )}
      <h3>Problems</h3>
      {d.problems.length ? (
        <ul>
          {d.problems.map((p) => (
            <li key={`${p.label}-${p.issue_id}`}>
              <PillTag pill={{ tone: p.tone, label: p.label }} /> {p.title} ({fmtInt(p.events)}){' '}
              <Link className="link" to={`/issues/${p.issue_id}`}>
                Issue #{p.issue_id}
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="muted">No new or regressed issues.</div>
      )}
      <h3>What customers want and we can't do yet</h3>
      <div className="muted">Needs AI analysis, which is off in this region.</div>
      <h3>Accounts to watch</h3>
      {d.accounts.length ? (
        <ul>
          {d.accounts.map((a) => (
            <li key={a.company_id}>
              <Link className="link" to={`/accounts/${a.company_id}`}>
                {a.name}
              </Link>{' '}
              {a.reason}
            </li>
          ))}
        </ul>
      ) : (
        <div className="muted">Nobody went quiet.</div>
      )}
      <div className="note" style={{ marginTop: 16 }}>
        A numbers-only digest: written prose needs AI analysis. Problems and accounts link to their pages.
      </div>
    </>
  );
}

export function DigestsPage() {
  const { kind, start } = useParams();
  const navigate = useNavigate();
  const list = useApi<DigestList>('/digests');
  const latest = list.data?.weeks[0];
  useEffect(() => {
    if (!kind && latest) navigate(`/digests/week/${latest.start}`, { replace: true });
  }, [kind, latest, navigate]);
  const preview = useApi<Digest>(`/digests/${kind ?? 'week'}`, { start }, { enabled: Boolean(kind && start) });
  return (
    <div className="side3">
      <aside className="rail">
        <div className="sec">Weekly digests</div>
        {list.data?.weeks.map((w) => (
          <Link
            key={w.start}
            to={`/digests/week/${w.start}`}
            className={cx('view', kind === 'week' && start === w.start && 'on')}
          >
            {w.label}
          </Link>
        ))}
        <div className="sec">Monthly</div>
        {list.data?.months.map((m) => (
          <Link
            key={m.start}
            to={`/digests/month/${m.start}`}
            className={cx('view', kind === 'month' && start === m.start && 'on')}
          >
            {m.label}
          </Link>
        ))}
        {list.error && <ErrorCard error={list.error} />}
      </aside>
      <div className="page digest">
        {kind && start ? (
          <DigestBody key={`${kind}-${start}`} kind={kind} start={start} />
        ) : (
          <Empty icon="✉" title="Pick a digest" />
        )}
      </div>
      <aside className="rail">
        <div className="sec">Delivery</div>
        <div className="setrow" title={LOGIN_NEEDED}>
          <span>Slack</span>
          <span className="sel" style={{ opacity: 0.5 }}>
            not set up
          </span>
        </div>
        <div className="setrow" title={LOGIN_NEEDED}>
          <span>Weekly</span>
          <span className="sel" style={{ opacity: 0.5 }}>
            Mon 09:00
          </span>
        </div>
        <div className="setrow" title={LOGIN_NEEDED}>
          <span>Monthly</span>
          <span style={{ opacity: 0.5 }}>
            <Toggle on={false} label="" onChange={() => undefined} />
          </span>
        </div>
        <div className="sec">Sections</div>
        {SECTIONS.map((s) => (
          <div key={s} className="chk on" title={LOGIN_NEEDED} style={{ cursor: 'not-allowed' }}>
            <span className="box">✓</span>
            {s}
          </div>
        ))}
        <div className="sec">Slack preview</div>
        {preview.data ? (
          <div className="slack">
            <div className="sh">
              <span className="sa" />
              <b>Compass Usage</b>
              <span className="faint" style={{ fontSize: 11 }}>
                APP
              </span>
            </div>
            <b>{preview.data.slack_preview.title}</b>
            {preview.data.slack_preview.lines.map((line) => (
              <div key={line}>{line}</div>
            ))}
            <span className="link" style={{ fontSize: 12 }}>
              Open the full digest →
            </span>
          </div>
        ) : (
          <div className="muted" style={{ fontSize: 12 }}>
            Pick a digest to preview it.
          </div>
        )}
        <div className="note">Slack delivery arrives in a later phase.</div>
      </aside>
    </div>
  );
}
